import React, { useState, useEffect } from 'react';
import { EmailTemplateType, EmailTemplate, PreviewResponse } from '../types/emailTemplate';
import { emailTemplateService } from '../services/emailTemplateService';

interface EmailTemplateEditorProps {
  templateType: EmailTemplateType;
  onSave: (template: Omit<EmailTemplate, 'type' | 'availableVariables'>) => Promise<void>;
}

export const EmailTemplateEditor: React.FC<EmailTemplateEditorProps> = ({
  templateType,
  onSave,
}) => {
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [plainTextBody, setPlainTextBody] = useState('');
  const [availableVariables, setAvailableVariables] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<PreviewResponse | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    loadTemplate();
  }, [templateType]);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      setError(null);
      const template = await emailTemplateService.getEmailTemplate(templateType);
      setSubject(template.subject);
      setHtmlBody(template.htmlBody);
      setPlainTextBody(template.plainTextBody);
      setAvailableVariables(template.availableVariables);
    } catch (err: any) {
      setError(err.message || 'Failed to load email template');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    try {
      setSaving(true);
      await onSave({ subject, htmlBody, plainTextBody });
      setSuccess('Email template saved successfully');
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save email template');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    setError(null);
    setPreviewData(null);

    try {
      setPreviewing(true);
      const preview = await emailTemplateService.previewEmailTemplate(templateType, {
        subject,
        htmlBody,
        plainTextBody,
      });
      setPreviewData(preview);
      setShowPreview(true);
    } catch (err: any) {
      setError(err.message || 'Failed to preview email template');
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const templateTypeName = templateType === 'welcome' ? 'Welcome Email' : 'Training Assignment Email';

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-800">{success}</p>
        </div>
      )}

      <div className="bg-white shadow rounded-lg p-6 space-y-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900">{templateTypeName}</h3>
          <p className="mt-1 text-sm text-gray-600">
            Customize the email template for {templateTypeName.toLowerCase()}
          </p>
        </div>

        {/* Available Variables */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Available Variables</h4>
          <div className="flex flex-wrap gap-2">
            {availableVariables.map((variable) => (
              <code
                key={variable}
                className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-mono"
              >
                {`{{.${variable}}}`}
              </code>
            ))}
          </div>
          <p className="mt-2 text-xs text-blue-800">
            Use these variables in your template. They will be replaced with actual values when the email is sent.
          </p>
        </div>

        {/* Link Syntax Documentation */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Link Syntax</h4>
          <p className="text-xs text-gray-700 mb-2">
            Use the following syntax to create clickable links in your emails:
          </p>
          <code className="block px-3 py-2 bg-gray-100 text-gray-800 rounded text-xs font-mono">
            [link site:https://example.com text:Click Here]
          </code>
          <p className="mt-2 text-xs text-gray-600">
            In HTML emails, this will be converted to a clickable link. In plain text emails, it will appear as "Click Here: https://example.com"
          </p>
        </div>

        {/* Subject Field */}
        <div>
          <label htmlFor={`subject-${templateType}`} className="block text-sm font-medium text-gray-700">
            Email Subject
          </label>
          <input
            type="text"
            id={`subject-${templateType}`}
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Enter email subject"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          />
        </div>

        {/* HTML Body Field */}
        <div>
          <label htmlFor={`html-body-${templateType}`} className="block text-sm font-medium text-gray-700">
            HTML Email Body
          </label>
          <textarea
            id={`html-body-${templateType}`}
            required
            value={htmlBody}
            onChange={(e) => setHtmlBody(e.target.value)}
            placeholder="Enter HTML email body"
            rows={10}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm font-mono text-xs"
          />
          <p className="mt-1 text-xs text-gray-500">
            Use HTML tags for formatting. Include template variables and link syntax as needed.
          </p>
        </div>

        {/* Plain Text Body Field */}
        <div>
          <label htmlFor={`plain-text-body-${templateType}`} className="block text-sm font-medium text-gray-700">
            Plain Text Email Body
          </label>
          <textarea
            id={`plain-text-body-${templateType}`}
            required
            value={plainTextBody}
            onChange={(e) => setPlainTextBody(e.target.value)}
            placeholder="Enter plain text email body"
            rows={10}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm font-mono text-xs"
          />
          <p className="mt-1 text-xs text-gray-500">
            Plain text version for email clients that don't support HTML. Include template variables and link syntax.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handlePreview}
            disabled={previewing || !subject || !htmlBody || !plainTextBody}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            {previewing ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Previewing...
              </>
            ) : (
              <>
                <svg
                  className="h-4 w-4 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
                Preview
              </>
            )}
          </button>

          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !subject || !htmlBody || !plainTextBody}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Template'}
          </button>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && previewData && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
              onClick={() => setShowPreview(false)}
            ></div>

            {/* Center modal */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="w-full mt-3 text-center sm:mt-0 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4" id="modal-title">
                      Email Preview
                    </h3>

                    {/* Subject Preview */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Subject:</label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm">
                        {previewData.renderedSubject}
                      </div>
                    </div>

                    {/* Sample Data */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sample Data Used:</label>
                      <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded text-xs font-mono">
                        {Object.entries(previewData.sampleData).map(([key, value]) => (
                          <div key={key}>
                            <span className="font-semibold">{key}:</span> {value}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Tabs for HTML and Plain Text */}
                    <div className="border-b border-gray-200 mb-4">
                      <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                        <button
                          onClick={() => {
                            const htmlTab = document.getElementById('preview-html-tab');
                            const plainTab = document.getElementById('preview-plain-tab');
                            const htmlContent = document.getElementById('preview-html-content');
                            const plainContent = document.getElementById('preview-plain-content');
                            
                            htmlTab?.classList.add('border-blue-500', 'text-blue-600');
                            htmlTab?.classList.remove('border-transparent', 'text-gray-500');
                            plainTab?.classList.remove('border-blue-500', 'text-blue-600');
                            plainTab?.classList.add('border-transparent', 'text-gray-500');
                            
                            htmlContent?.classList.remove('hidden');
                            plainContent?.classList.add('hidden');
                          }}
                          id="preview-html-tab"
                          className="border-blue-500 text-blue-600 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm"
                        >
                          HTML Version
                        </button>
                        <button
                          onClick={() => {
                            const htmlTab = document.getElementById('preview-html-tab');
                            const plainTab = document.getElementById('preview-plain-tab');
                            const htmlContent = document.getElementById('preview-html-content');
                            const plainContent = document.getElementById('preview-plain-content');
                            
                            plainTab?.classList.add('border-blue-500', 'text-blue-600');
                            plainTab?.classList.remove('border-transparent', 'text-gray-500');
                            htmlTab?.classList.remove('border-blue-500', 'text-blue-600');
                            htmlTab?.classList.add('border-transparent', 'text-gray-500');
                            
                            plainContent?.classList.remove('hidden');
                            htmlContent?.classList.add('hidden');
                          }}
                          id="preview-plain-tab"
                          className="border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm"
                        >
                          Plain Text Version
                        </button>
                      </nav>
                    </div>

                    {/* HTML Preview */}
                    <div id="preview-html-content" className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">HTML Email Body:</label>
                      <div className="border border-gray-300 rounded p-4 bg-white max-h-96 overflow-y-auto">
                        <div dangerouslySetInnerHTML={{ __html: previewData.renderedHtml }} />
                      </div>
                    </div>

                    {/* Plain Text Preview */}
                    <div id="preview-plain-content" className="mb-4 hidden">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Plain Text Email Body:</label>
                      <div className="border border-gray-300 rounded p-4 bg-gray-50 max-h-96 overflow-y-auto">
                        <pre className="whitespace-pre-wrap text-sm font-mono">{previewData.renderedPlainText}</pre>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setShowPreview(false)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
